import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppRoutes, AuthRoutes } from '@core';
import { Subscription, interval, take } from 'rxjs';
import { ResetPasswordHandler } from '../../application/handlers/reset-password.handler';

const RESEND_COUNTDOWN_SECONDS: number = 60;

@Component({
  selector: 'auth-reset-password-sent-page',
  templateUrl: './reset-password-sent.page.html',
  styleUrls: ['./reset-password-sent.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ResetPasswordSentPage implements OnInit, OnDestroy {
  readonly loginPath: string = `/${AppRoutes.AUTH}/${AuthRoutes.LOGIN}`;
  readonly resetPath: string = `/${AppRoutes.AUTH}/${AuthRoutes.RESET_PASSWORD}`;

  readonly email: WritableSignal<string> = signal<string>('');
  readonly countdown: WritableSignal<number> = signal<number>(RESEND_COUNTDOWN_SECONDS);
  readonly resending: WritableSignal<boolean> = signal<boolean>(false);
  readonly errorMessage: WritableSignal<string | null> = signal<string | null>(null);

  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly resetPasswordHandler: ResetPasswordHandler = inject(ResetPasswordHandler);

  private tickSub: Subscription | null = null;

  ngOnInit(): void {
    const emailParam: string | null = this.route.snapshot.queryParamMap.get('email');
    if (!emailParam) {
      void this.router.navigate([this.resetPath]);
      return;
    }
    this.email.set(emailParam);
    this.startCountdown();
  }

  ngOnDestroy(): void {
    this.tickSub?.unsubscribe();
  }

  goBack(): void {
    void this.router.navigate([this.resetPath]);
  }

  onResend(): void {
    if (this.countdown() > 0 || this.resending()) return;
    this.resending.set(true);
    this.errorMessage.set(null);
    this.resetPasswordHandler
      .handle({ email: this.email() })
      .pipe(take(1))
      .subscribe({
        next: (): void => {
          this.resending.set(false);
          this.startCountdown();
        },
        error: (err: Error): void => {
          this.resending.set(false);
          this.errorMessage.set(err.message);
        }
      });
  }

  private startCountdown(): void {
    this.tickSub?.unsubscribe();
    this.countdown.set(RESEND_COUNTDOWN_SECONDS);
    this.tickSub = interval(1000).subscribe((): void => {
      const next: number = this.countdown() - 1;
      this.countdown.set(next > 0 ? next : 0);
      if (next <= 0) this.tickSub?.unsubscribe();
    });
  }
}
